import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import { Alert } from '@brokerbay/design-components';
import {
  TEMPLATE_ROLE_PRIORITY_MAP,
  TemplateRoles,
  TemplateUsage,
} from '@bb/domain/dist/types';
import { FroalaTaggedInput, message as antMessage } from 'components';
import { replaceTagsWithMarkup, defaultTemplates, sortTemplates } from 'utils';
import { TemplateDropdownType } from '@app/template/TemplateDropdown';
import TemplateHeader from '../../../../Router/pages/TemplateManager/TemplateHeader';

export interface FeedbackMessageEditorProps {
  templates: TemplateDropdownType;
  templateId?: string;
  subject?: string;
  body?: string;
  signature: string;
  settingsId?: string;
  setSubject: (value: string) => void;
  setBody: (value: string) => void;
  setTemplateId: (id: string) => void;
  setIsCustomSelected: (isCustomSelected: boolean) => void;
  dataCy?: string;
}

const FeedbackMessageEditor = (props: FeedbackMessageEditorProps) => {
  const {
    templates,
    templateId,
    subject,
    body,
    signature,
    settingsId,
    setSubject,
    setBody,
    setTemplateId,
    setIsCustomSelected,
    dataCy,
  } = props;

  const processedTemplates = useMemo(() => {
    let processed = cloneDeep(templates);
    // Add a root default template if necessary
    if (!processed.find(t => t.isDefault)) {
      processed.push({
        ...defaultTemplates.feedback,
        body: defaultTemplates.feedback.body.concat(signature),
        id: 'default',
        name: 'Default Feedback Template',
        type: 'feedback',
        isDefault: true,
        role: 'GLOBAL',
        updatedAt: undefined,
      });
    }
    // 1.user default 2.team default 3.branch default 4.global default, 5. last edited
    processed = sortTemplates(processed);
    // Add a custom template selection
    const custom = {
      subject: '',
      body: (settingsId && body) || signature,
      id: 'custom',
      name: 'Custom',
      type: 'feedback' as TemplateUsage,
      role: 'USER' as TemplateRoles,
    };
    processed.push(custom);

    return processed;
  }, [templates]);

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [previewing, setPreviewing] = useState(false);
  // FIXME: Refactor Parent Component into React
  const [reactSubject, setReactSubject] = useState(subject);
  const [reactBody, setReactBody] = useState(body);

  const changeTemplate = (id: string) => {
    const newTemplate = processedTemplates.some(t => t.id === id);
    if (!newTemplate) return;

    setSelectedTemplate(id);
    setTemplateId(id);
  };

  useEffect(() => {
    const primaryDefaultId = processedTemplates
      .filter(t => t.isDefault)
      .sort(
        (a, b) =>
          TEMPLATE_ROLE_PRIORITY_MAP[a.role] -
          TEMPLATE_ROLE_PRIORITY_MAP[b.role],
      )?.[0].id;
    const id = templateId || (settingsId && 'custom') || primaryDefaultId;
    setSelectedTemplate(id);
    setTemplateId(id);
  }, [processedTemplates]);

  useEffect(() => {
    const newTemplate = processedTemplates.find(t => t.id === selectedTemplate);
    setReactBody(newTemplate?.body || '');
  }, [selectedTemplate, processedTemplates]);

  useEffect(() => {
    setSubject(reactSubject);
  }, [reactSubject]);

  useEffect(() => {
    setBody(reactBody);
  }, [reactBody]);

  const onClickDisabledInput = () => {
    antMessage.warning({
      content: (
        <span>
          Click <strong>Exit Preview</strong> to make changes.
        </span>
      ),
    });
  };

  return (
    <div data-cy={dataCy}>
      <AlertContainer>
        <Alert
          message={
            selectedTemplate !== 'custom' ? (
              <>
                This is an existing template and cannot be edited. To edit
                templates, click on the <strong>Manage Templates</strong> button
                to be redirected to the Template Manager.
              </>
            ) : (
              'The feedback template below will be delivered to showing agents following their showing, asking them to provide feedback.'
            )
          }
          type={selectedTemplate !== 'custom' ? 'warning' : 'info'}
          showIcon
          dataCy={`${dataCy}-alert`}
        />
      </AlertContainer>
      <TemplateHeader
        templates={processedTemplates}
        selectedTemplate={selectedTemplate}
        subject={reactSubject}
        previewing={previewing}
        onPreview={setPreviewing}
        onDropdownChange={changeTemplate}
        onInputChange={e => {
          const { value } = e.target;
          setIsCustomSelected(true);
          setReactSubject(value);
        }}
        limitWidth
        dataCy={`${dataCy}-template-header`}
      />
      <FroalaTaggedInput
        model={replaceTagsWithMarkup(selectedTemplate ? reactBody : '')}
        onModelChange={(templateBody: any) => {
          setIsCustomSelected(true);
          setReactBody(templateBody);
        }}
        templateType="feedback"
        preview={previewing}
        onClick={() => {
          if (previewing) onClickDisabledInput();
        }}
        disabled={selectedTemplate !== 'custom'}
        dataCy={`${dataCy}-froala-tagged-input`}
      />
    </div>
  );
};

export default FeedbackMessageEditor;

const AlertContainer = styled.div`
  margin-bottom: 16px;
`;
